import { Directive, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import {
    FadeKeyframes,
    ReducedMotion,
    RevealCardTiming,
    RevealKeyframes,
    RevealTiming,
    RiseKeyframes,
    RiseTiming,
    FADE_MS,
    RISE_EASING,
} from './rise';

/**
 * Plays a Web Animation on `element` unless the user prefers reduced motion. The Explorer is zoneless, so these
 * directives never lean on zone change detection; they react to input changes and run animate() directly.
 */
function Play(element: HTMLElement, keyframes: Keyframe[], options: KeyframeAnimationOptions, into: Animation[]): void {
    if (ReducedMotion() || typeof element.animate !== 'function') return;
    into.push(element.animate(keyframes, options));
}

function CancelAll(animations: Animation[]): void {
    for (const a of animations) a.cancel();
    animations.length = 0;
}

/** `[aguRise]="index"`: rises on init and whenever `RiseKey` changes (the result-set stamp). P/Member_Hub.html:1807-1845 */
@Directive({ selector: '[aguRise]', standalone: false })
export class AGURiseDirective implements OnInit, OnChanges, OnDestroy {
    @Input('aguRise') Index: number | '' = 0;
    @Input() RiseDistance = 8;
    @Input() RiseKey: number | string | null = null;
    private readonly animations: Animation[] = [];
    private started = false;

    constructor(private readonly host: ElementRef<HTMLElement>) {}

    ngOnInit(): void {
        this.started = true;
        this.Run();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // The first RiseKey binding lands before ngOnInit; ngOnInit runs once for it.
        if (this.started && changes['RiseKey'] && !changes['RiseKey'].firstChange) this.Run();
    }

    ngOnDestroy(): void {
        CancelAll(this.animations);
    }

    private Run(): void {
        CancelAll(this.animations);
        const index = typeof this.Index === 'number' ? this.Index : 0;
        Play(this.host.nativeElement, RiseKeyframes(this.RiseDistance), RiseTiming(index), this.animations);
    }
}

/**
 * `[aguReveal]="direction"` with `RevealKey`: the incoming finder section slides +-14px by tab order and its first 8
 * cards rise behind it. P/grant-finder.html:1219-1232
 */
@Directive({ selector: '[aguReveal]', standalone: false })
export class AGURevealDirective implements OnChanges, OnDestroy {
    @Input('aguReveal') Direction = 0;
    @Input() RevealKey: number | string | null = null;
    /** Selector for the cards that follow a beat behind. */
    @Input() RevealItems = ':scope > *';
    private readonly animations: Animation[] = [];
    private destroyed = false;

    constructor(private readonly host: ElementRef<HTMLElement>) {}

    ngOnChanges(changes: SimpleChanges): void {
        // firstChange is the page booting, not a tab switch (P/grant-finder.html:1220 `!booted`).
        if (changes['RevealKey'] && !changes['RevealKey'].firstChange) this.Run();
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        CancelAll(this.animations);
    }

    private Run(): void {
        CancelAll(this.animations);
        const el = this.host.nativeElement;
        Play(el, RevealKeyframes(this.Direction), RevealTiming(), this.animations);
        // This hook runs before the host's content re-renders; the incoming cards exist once the pass completes.
        queueMicrotask(() => {
            if (this.destroyed) return;
            const items = Array.from(el.querySelectorAll<HTMLElement>(this.RevealItems)).slice(0, 8);
            items.forEach((item, i) => Play(item, RiseKeyframes(8), RevealCardTiming(i), this.animations));
        });
    }
}

/** `[aguFade]="hidden"`: a 220ms opacity fade when `hidden` flips false (or the block first renders shown). P/Member_Hub.html:1815, :1829 */
@Directive({ selector: '[aguFade]', standalone: false })
export class AGUFadeDirective implements OnInit, OnChanges, OnDestroy {
    @Input('aguFade') Hidden: boolean | '' = false;
    private readonly animations: Animation[] = [];

    constructor(private readonly host: ElementRef<HTMLElement>) {}

    ngOnInit(): void {
        if (this.Hidden !== true) this.Run();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const c = changes['Hidden'];
        if (c && !c.firstChange && c.previousValue === true && c.currentValue !== true) this.Run();
    }

    ngOnDestroy(): void {
        CancelAll(this.animations);
    }

    private Run(): void {
        CancelAll(this.animations);
        Play(this.host.nativeElement, FadeKeyframes(), { duration: FADE_MS, easing: RISE_EASING }, this.animations);
    }
}
